import {
  balanceCommunicationsId,
  balanceNotificationActive,
  balanceNotificationInput,
  winningBetsCommunicationsId,
  winningBetsNotificationActive,
} from 'components/Communications/Communications.config';
import { CommunicationsFormValues } from 'components/Communications/Communications.types';
import { produce } from 'immer';
import { memoize } from 'lodash-es';
import {
  AMOUNT_PLAYED_NOTIFICATION_ID,
  amountPlayedNotificationActive,
  rechargeLimitInput,
} from 'pages/PlayResponsabilityPage/partials/PlayResponsibly.config';
import {
  AmountPlayedNotificationFormValues,
  CommunicationRequestPayload,
  CommunicationsRequestPayload,
  SelfLimitationFormValues,
  SelfLimitationRequestPayload,
} from 'pages/PlayResponsabilityPage/partials/PlayResponsibly.types';
import colors from 'style-utils/colors';
import { Theme } from 'theme';

export type SelfLimitationFormStateConfig = Readonly<{
  formValues: SelfLimitationFormValues;
}>;

export type AmountPlayedNotificationFormStateConfig = Readonly<{
  formValues: AmountPlayedNotificationFormValues;
}>;

export type CommunicationsFormStateConfig = Readonly<{
  formValues: CommunicationsFormValues;
}>;

export function postContextSensitiveSelfLimitationRequestData(
  config: Required<SelfLimitationFormStateConfig>,
  rechargeLimitType: number,
): SelfLimitationRequestPayload {
  const { formValues } = config;
  const { [rechargeLimitInput]: threshold } = formValues;
  const type = rechargeLimitType;

  return {
    threshold,
    type,
  } as SelfLimitationRequestPayload;
}

export function postContextSensitiveAmountPlayedNotificationRequestData(
  config: Required<AmountPlayedNotificationFormStateConfig>,
): CommunicationRequestPayload {
  const { formValues } = config;
  const { [amountPlayedNotificationActive]: active } = formValues;
  const id = AMOUNT_PLAYED_NOTIFICATION_ID;
  const channels = 1;
  const refType = 1;
  const status = active ? 1 : 0;
  const refValue = 0;

  return {
    channels,
    id,
    refType,
    refValue,
    status,
  } as CommunicationRequestPayload;
}

export function postContextSensitiveCommunicationsRequestData(
  config: Required<CommunicationsFormStateConfig>,
): CommunicationsRequestPayload {
  const { formValues } = config;
  const {
    [balanceNotificationActive]: balanceNotificationInputActive,
    [balanceNotificationInput]: refValue,
    [winningBetsNotificationActive]: winningBetsNotificationInputActive,
  } = formValues;

  const channels = 1;
  const refType = 1;

  const balanceNotificationData = {
    channels,
    id: balanceCommunicationsId,
    refType,
    refValue: balanceNotificationInputActive ? refValue : 0,
    status: balanceNotificationInputActive ? 1 : 0,
  } as CommunicationRequestPayload;

  const winningBetsNotificationData = {
    channels,
    id: winningBetsCommunicationsId,
    refType,
    refValue: 0,
    status: winningBetsNotificationInputActive ? 1 : 0,
  } as CommunicationRequestPayload;

  return {
    communications: [balanceNotificationData, winningBetsNotificationData],
  } as CommunicationsRequestPayload;
}

export const GameBreakCheckboxTheme: (globalTheme: Theme) => Theme = memoize(
  (globalTheme) =>
    produce(globalTheme, (draft) => {
      draft.checkbox.root.minHeight = draft.checkbox.root.lineHeight = '20px';
      draft.checkbox.root.paddingLeft =
        draft.checkbox.root.tablet.paddingLeft =
        draft.checkbox.root.desktop.paddingLeft =
          '20px';

      draft.checkbox.checkmark.height = draft.checkbox.checkmark.width = '20px';
      draft.checkbox.checkmark.boxShadow = '';
      draft.checkbox.checkmark.border = `2px solid ${colors.neutralMediumGray}`;
      draft.checkbox.checkmark.checkedBorder = `0px solid ${colors.neutralMediumGray}`;

      draft.checkbox.checkmark.borderRadius = '4px';
      draft.checkbox.checkmark.after.height =
        draft.checkbox.checkmark.after.width = '20px';
      draft.checkbox.checkmark.after.backgroundSize = '20px';
    }),
);

export function getFormattedDate(date: Date) {
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function getFormattedTime(date: Date) {
  return date.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
